import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./PasswordGenerator.css";
import Nav from "../general/nav.jsx";
import AboveFooter from "../general/AboveFooter.jsx";
import Footer from "../general/Footer.jsx";
import VioletHaloBackground from "../hook/effects/HaloViolet.jsx";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import CopyIcon from "../../assets/img/Ico/Copy.svg";

gsap.registerPlugin(ScrollTrigger);

export default function PasswordGenerator() {
  const { t } = useTranslation();
  const [options, setOptions] = useState({
    lowercase: true,
    uppercase: true,
    numbers: true,
    specials: false,
  });
  const [length, setLength] = useState(12);
  const [quantity, setQuantity] = useState(1);
  const [passwords, setPasswords] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const notificationRef = useRef(null);
  const optionsRef = useRef(null);

  // Animation de la notification
  useEffect(() => {
    if (showNotification && notificationRef.current) {
      gsap.fromTo(
        notificationRef.current,
        { y: -100, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: "power2.out" }
      );
    }
  }, [showNotification]);

  // Animation des options (checkboxes) au scroll
  useEffect(() => {
    if (!optionsRef.current) return;

    const labels = optionsRef.current.querySelectorAll("label");

    gsap.fromTo(
      labels,
      {
        y: 30,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        ease: "power2.out",
        stagger: 0.15, // Délai de 0.15s entre chaque checkbox
        scrollTrigger: {
          trigger: optionsRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );
  }, []);

  const generatePassword = () => {
    // enforce safe bounds in case values were set programmatically
    const safeLength = Number.isNaN(length)
      ? 12
      : Math.min(100, Math.max(1, length));
    const safeQuantity = Number.isNaN(quantity)
      ? 1
      : Math.min(100, Math.max(1, quantity));
    const chars = {
      lowercase: "abcdefghijklmnopqrstuvwxyz",
      uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      numbers: "0123456789",
      specials: "!@#$%^&*()_+-=[]{}|;:,.<>?",
    };

    let charPool = "";
    if (options.lowercase) charPool += chars.lowercase;
    if (options.uppercase) charPool += chars.uppercase;
    if (options.numbers) charPool += chars.numbers;
    if (options.specials) charPool += chars.specials;

    if (charPool === "") {
      alert("Veuillez sélectionner au moins une option.");
      return;
    }

    const newPasswords = [];
    for (let i = 0; i < safeQuantity; i++) {
      let password = "";
      for (let j = 0; j < safeLength; j++) {
        password += charPool[Math.floor(Math.random() * charPool.length)];
      }
      newPasswords.push(password);
    }
    setPasswords(newPasswords);
  };

  const copyToClipboard = async (password) => {
    try {
      // Preferred modern API (only available on secure contexts like https or localhost)
      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        await navigator.clipboard.writeText(password);
      } else {
        // Fallback for insecure contexts or older browsers
        const textarea = document.createElement("textarea");
        textarea.value = password;
        // Make element readonly to avoid iOS keyboard opening, and push it off-screen
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);

        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);

        const successful = document.execCommand("copy");
        document.body.removeChild(textarea);

        if (!successful) throw new Error("Fallback: copy command failed");
      }

      setShowNotification(true);
      setTimeout(() => {
        if (notificationRef.current) {
          gsap.to(notificationRef.current, {
            y: -100,
            opacity: 0,
            duration: 0.5,
            ease: "power2.in",
            onComplete: () => setShowNotification(false),
          });
        }
      }, 2500); // Start exit animation after 2.5 seconds
    } catch (err) {
      // Graceful error handling: log and inform the user
      // eslint-disable-next-line no-console
      console.error("Copy to clipboard failed:", err);
      // Try to show a message to the user; fallback to alert if translation missing
      const msg =
        t("passwordGenerator.copy_failed") ||
        "Impossible de copier le mot de passe.";
      // If notifications are used elsewhere, you could reuse that; simple alert is sufficient here
      alert(msg);
    }
  };

  return (
    <>
      <Nav />
      {showNotification && (
        <div ref={notificationRef} className="password-generator-notification">
          Password copied!
        </div>
      )}
      <div className="password-generator">
        <h1>{t("passwordGenerator.title")}</h1>
        <div className="options" ref={optionsRef}>
          <label>
            <input
              type="checkbox"
              checked={options.lowercase}
              onChange={(e) =>
                setOptions({ ...options, lowercase: e.target.checked })
              }
            />
            {t("passwordGenerator.lowercase")}
          </label>
          <label>
            <input
              type="checkbox"
              checked={options.uppercase}
              onChange={(e) =>
                setOptions({ ...options, uppercase: e.target.checked })
              }
            />
            {t("passwordGenerator.uppercase")}
          </label>
          <label>
            <input
              type="checkbox"
              checked={options.numbers}
              onChange={(e) =>
                setOptions({ ...options, numbers: e.target.checked })
              }
            />
            {t("passwordGenerator.numbers")}
          </label>
          <label>
            <input
              type="checkbox"
              checked={options.specials}
              onChange={(e) =>
                setOptions({ ...options, specials: e.target.checked })
              }
            />
            {t("passwordGenerator.specials")}
          </label>
        </div>
        <div className="inputs">
          <label>
            {t("passwordGenerator.length")} :
            <input
              type="number"
              value={length}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                const clamped = Number.isNaN(v)
                  ? 1
                  : Math.min(35, Math.max(1, v));
                setLength(clamped);
              }}
              min="1"
              max="100"
            />
          </label>
          <label>
            {t("passwordGenerator.quantity")}
            <input
              type="number"
              value={quantity}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                const clamped = Number.isNaN(v)
                  ? 1
                  : Math.min(100, Math.max(1, v));
                setQuantity(clamped);
              }}
              min="1"
              max="100"
            />
          </label>
        </div>
        <button className="websiteBtn" onClick={generatePassword}>
          {t("passwordGenerator.generate")}
        </button>
        {passwords.length > 0 && (
          <h2>{t("passwordGenerator.generatedTitle")}</h2>
        )}
        <div className="passwords">
          {passwords.map((pwd, index) => (
            <div key={index} className="password-item">
              <span>{pwd}</span>
              <button onClick={() => copyToClipboard(pwd)}>
                <img src={CopyIcon} alt={t("passwordGenerator.copy")} />
              </button>
            </div>
          ))}
        </div>
        <div className="disclaimer">
          <p>{t("passwordGenerator.disclaimer")}</p>
        </div>
      </div>
      <AboveFooter />
      <Footer />
    </>
  );
}
